import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { createSlice } from '@reduxjs/toolkit';

// Функция возвращает текущий origin: protocol + '//' + hostname[:port]
// Если задана переменная окружения REACT_APP_API_URL, используется она (для тестовой среды)
// Иначе URL берётся из адресной строки браузера (для продакшна)
const getOrigin = () => {
  // Проверяем наличие явно указанного API URL в переменных окружения
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Иначе берём из адресной строки
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  return ''; // на случай SSR или тестов
};

// Начальное состояние
const initialState = {
  API_URL: `${getOrigin()}/client/api/`,
  API_URL_NO_RA: `${getOrigin()}/`,
  API_URL_NO_RAN: `${getOrigin()}/`,
  APP_THEME: 'dark',
  BOT_NOTIFIER: '',
  BOT_NOTIFIER_ERROR: '',
  SUPPORT_BOT: '',
  SUPPORT_BOT_LINK: '',
  SUPPORT_BOT_CONNECTED: false,
  SUPPORT_BOT_TOPIC_ACTIVE: false,
  SUPPORT_BOT_MODE: 'disabled',
  REQUISITE_DATA_TO_EDIT: {},
  USER_PAYMENTS: [],
  USER_CODE: '',
  USER_CHAT: '',
  USER_ID: null,
  USER_ADD_BALANCE_WALLET: '',
  SHOW_SIDEBAR: false,
  IS_ACTIVE_USER: false,
  USER_ROLE: '',
  USER_ROLE_INCLUDE_BOOST: '',
  USER_CAN_CHANGE_AMOUNT: 'no',
  USER_CAN_SET_MIN_ORDERS: 'no',
  USER_CAN_DELETE_OWN_REQUISITES: false,
  PROVIDER: '',
  USER_NAME: '',
  ADD_BALANCE_LAST_TIME: '',
  BOOST_ACCOUNT_NEXT_TIME: '',
  RATES: [],
  ACTIVE_PAYOUTS_COUNT: 0,
  ACTIVE_REQUESTS_COUNT: 0,
  ACTIVE_CREDENTIALS_COUNT: 0,
  PAYOUT_SETTINGS_ENABLED: false,
  HAS_PAYOUT_SETTINGS: false,
  USDT_BALANCE: 0,
  IS_FROZEN: false,
  FREEZE_KIND: '',
  NOVODROID_TOKEN: '',
  NOVODROID_URL: '',
  // Layout state
  SIDEBAR_COLLAPSED: false,
  SIDEBAR_MOBILE_OPEN: false,
  // Настройки React-клиента из ProjectSettings
  CLIENT_APP_TITLE: 'Matrix Exchange Panel',
  CLIENT_SIDEBAR_NAME: 'MATRIX',
  CLIENT_SIDEBAR_SUBTITLE: 'exchange',
  CLIENT_WELCOME_MESSAGE: 'Welcome to the Matrix',
  CLIENT_FAVICON: null,
  CLIENT_BACKGROUND_ANIMATION: 'matrixRain',
  CLIENT_BLEND_MODE: 'soft-light',
  // Маппинг банков по валютам
  BANK_CURRENCIES: {},
  DEFAULT_BANK_CURRENCY: 'RUB',
  // Банки без обязательного last4digits при автозакрытии
  BANKS_WITHOUT_CARD: [],
  // Банки, требующие iban_last4digits (рахунок)
  BANKS_WITH_IBAN: [],
  // Счётчик непрочитанных новостей
  UNREAD_NEWS_COUNT: 0,
  // Регламент работы
  HAS_REGULATION: false,
  IS_SUPERUSER: false,
  // Настройки автообновления
  REFRESH_INTERVAL: parseInt(localStorage.getItem('refreshInterval')) || 20000, // по умолчанию 20 секунд
  IS_REFRESHING: false,
};

// Создание slice
const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setAppTheme: (state, action) => {
      state.APP_THEME = action.payload;
    },
    setDataOfReqToEdit: (state, action) => {
      state.REQUISITE_DATA_TO_EDIT = action.payload;
    },
    setUserCode: (state, action) => {
      state.USER_CODE = action.payload;
    },
    setUserChat: (state, action) => {
      state.USER_CHAT = action.payload;
    },
    setUserID: (state, action) => {
      state.USER_ID = action.payload;
    },
    setUserAddBalanceWallet: (state, action) => {
      state.USER_ADD_BALANCE_WALLET = action.payload;
    },
    setShowSidebar: (state, action) => {
      state.SHOW_SIDEBAR = action.payload;
    },
    setIsUserActive: (state, action) => {
      state.IS_ACTIVE_USER = action.payload;
    },
    setUserRole: (state, action) => {
      state.USER_ROLE = action.payload;
    },
    setProvider: (state, action) => {
      state.PROVIDER = action.payload; 
    },
    setUserName: (state, action) => {
      state.USER_NAME = action.payload; 
    },
    setUserAvailablePayments: (state, action) => {
      state.USER_PAYMENTS = action.payload;
    },
    setNotifierBot: (state, action) => {
      state.BOT_NOTIFIER = action.payload;
    },
    setNotifierBotError: (state, action) => {
      state.BOT_NOTIFIER_ERROR = action.payload;
    },
    setSupportBot: (state, action) => {
      state.SUPPORT_BOT = action.payload;
    },
    setSupportBotLink: (state, action) => {
      state.SUPPORT_BOT_LINK = action.payload;
    },
    setSupportBotConnected: (state, action) => {
      state.SUPPORT_BOT_CONNECTED = action.payload;
    },
    setSupportBotTopicActive: (state, action) => {
      state.SUPPORT_BOT_TOPIC_ACTIVE = action.payload;
    },
    setSupportBotMode: (state, action) => {
      state.SUPPORT_BOT_MODE = action.payload;
    },
    setUserRoleIncludeBoost: (state, action) => {
      state.USER_ROLE_INCLUDE_BOOST = action.payload;
    },
    setReduxUserCanChangeAmount: (state, action) => {
      state.USER_CAN_CHANGE_AMOUNT = action.payload;
    },
    setReduxUserCanSetMinOrders: (state, action) => {
      state.USER_CAN_SET_MIN_ORDERS = action.payload;
    },
    setUserCanDeleteOwnRequisites: (state, action) => {
      state.USER_CAN_DELETE_OWN_REQUISITES = action.payload;
    },
    setLastTimeOfAddBalance: (state, action) => {
      state.ADD_BALANCE_LAST_TIME = action.payload; 
    },
    setNextTimeOfBoostAccount: (state, action) => {
      state.BOOST_ACCOUNT_NEXT_TIME = action.payload; 
    },
    setRates: (state, action) => {
      state.RATES = action.payload;
    },
    setActivePayoutsCount: (state, action) => {
      state.ACTIVE_PAYOUTS_COUNT = action.payload;
    },
    setActiveRequestsCount: (state, action) => {
      state.ACTIVE_REQUESTS_COUNT = action.payload;
    },
    setActiveCredentialsCount: (state, action) => {
      state.ACTIVE_CREDENTIALS_COUNT = action.payload;
    },
    setPayoutSettingsEnabled: (state, action) => {
      state.PAYOUT_SETTINGS_ENABLED = action.payload;
    },
    setHasPayoutSettings: (state, action) => {
      state.HAS_PAYOUT_SETTINGS = action.payload;
    },
    setUsdtBalance: (state, action) => {
      state.USDT_BALANCE = action.payload;
    },
    setIsFrozen: (state, action) => {
      state.IS_FROZEN = action.payload;
    },
    setFreezeKind: (state, action) => {
      state.FREEZE_KIND = action.payload;
    },
    setNovodroidToken: (state, action) => {
      state.NOVODROID_TOKEN = action.payload;
    },
    setNovodroidUrl: (state, action) => {
      state.NOVODROID_URL = action.payload;
    },
    // Layout reducers
    setSidebarCollapsed: (state, action) => {
      state.SIDEBAR_COLLAPSED = action.payload;
    },
    setSidebarMobileOpen: (state, action) => {
      state.SIDEBAR_MOBILE_OPEN = action.payload;
    },
    // Reducers для настроек React-клиента
    setClientAppTitle: (state, action) => {
      state.CLIENT_APP_TITLE = action.payload;
    },
    setClientSidebarName: (state, action) => {
      state.CLIENT_SIDEBAR_NAME = action.payload;
    },
    setClientSidebarSubtitle: (state, action) => {
      state.CLIENT_SIDEBAR_SUBTITLE = action.payload;
    },
    setClientWelcomeMessage: (state, action) => {
      state.CLIENT_WELCOME_MESSAGE = action.payload;
    },
    setClientFavicon: (state, action) => {
      state.CLIENT_FAVICON = action.payload;
    },
    setClientBackgroundAnimation: (state, action) => {
      state.CLIENT_BACKGROUND_ANIMATION = action.payload;
    },
    setClientBlendMode: (state, action) => {
      state.CLIENT_BLEND_MODE = action.payload;
    },
    // Reducers для маппинга банков
    setBankCurrencies: (state, action) => {
      state.BANK_CURRENCIES = action.payload;
    },
    setDefaultBankCurrency: (state, action) => {
      state.DEFAULT_BANK_CURRENCY = action.payload;
    },
    setBanksWithoutCard: (state, action) => {
      state.BANKS_WITHOUT_CARD = action.payload;
    },
    setBanksWithIban: (state, action) => {
      state.BANKS_WITH_IBAN = action.payload;
    },
    // Reducers для автообновления
    setRefreshInterval: (state, action) => {
      state.REFRESH_INTERVAL = action.payload;
      localStorage.setItem('refreshInterval', action.payload);
    },
    setIsRefreshing: (state, action) => {
      state.IS_REFRESHING = action.payload;
    },
    setUnreadNewsCount: (state, action) => {
      state.UNREAD_NEWS_COUNT = action.payload;
    },
    setHasRegulation: (state, action) => {
      state.HAS_REGULATION = action.payload;
    },
    setIsSuperuser: (state, action) => {
      state.IS_SUPERUSER = action.payload;
    },
  }
});

// Экспортируем экшены
export const {
  setAppTheme,
  setDataOfReqToEdit,
  setUserCode,
  setUserID,
  setUserChat,
  setUserAddBalanceWallet,
  setShowSidebar,
  setIsUserActive,
  setUserRole,
  setProvider,
  setUserName,
  setUserRoleIncludeBoost,
  setReduxUserCanChangeAmount,
  setReduxUserCanSetMinOrders,
  setUserCanDeleteOwnRequisites,
  setLastTimeOfAddBalance,
  setNextTimeOfBoostAccount,
  setUserAvailablePayments,
  setRates,
  setNotifierBot,
  setNotifierBotError,
  setSupportBot,
  setSupportBotLink,
  setSupportBotConnected,
  setSupportBotTopicActive,
  setSupportBotMode,
  setActivePayoutsCount,
  setActiveRequestsCount,
  setActiveCredentialsCount,
  setPayoutSettingsEnabled,
  setHasPayoutSettings,
  setUsdtBalance,
  setIsFrozen,
  setFreezeKind,
  setNovodroidToken,
  setNovodroidUrl,
  // Layout actions
  setSidebarCollapsed,
  setSidebarMobileOpen,
  // Экспорт actions для настроек React-клиента
  setClientAppTitle,
  setClientSidebarName,
  setClientSidebarSubtitle,
  setClientWelcomeMessage,
  setClientFavicon,
  setClientBackgroundAnimation,
  setClientBlendMode,
  // Экспорт actions для маппинга банков
  setBankCurrencies,
  setDefaultBankCurrency,
  setBanksWithoutCard,
  setBanksWithIban,
  // Экспорт actions для автообновления
  setRefreshInterval,
  setIsRefreshing,
  // Новости
  setUnreadNewsCount,
  // Регламент
  setHasRegulation,
  // Суперадмин
  setIsSuperuser,
} = appSlice.actions;

// Конфигурация redux-persist
const persistConfig = {
  key: 'root',
  storage,
};

// Создаём persistReducer
const persistedReducer = persistReducer(persistConfig, appSlice.reducer);

// Создаём store
const store = configureStore({
  reducer: persistedReducer,
  devTools: false,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

// Экспортируем store по умолчанию
export default store;

// Экспортируем persistor
export const persistor = persistStore(store);
